import React, {useMemo, useState} from 'react';
import {collection, getFirestore, limit, orderBy, query, where} from "firebase/firestore";
import {useCollection} from "react-firebase-hooks/firestore";
import {useNavigate} from "react-router-dom";
import moment from "moment";
import Firebase from "../../Firebase/Firebase";

const db = getFirestore(Firebase);

/**
 * Live activity (adminActivity, written by Cloud Functions — admin_events.js)
 * and a leaderboard for today / this week. Updates in real time.
 */
const FILTERS = [
    {key: 'all', label: 'All'},
    {key: 'orders', label: 'Orders'},
    {key: 'registrations', label: 'Sign-ups'},
    {key: 'payouts', label: 'Payouts'},
    {key: 'support', label: 'Support'},
] as const;

const DOT: Record<string, string> = {orders: '#16a34a', registrations: '#2563eb', payouts: '#9333ea', support: '#ea580c'};
const mwk = (n: number) => `MWK ${Math.round(n).toLocaleString('en-US')}`;
const card: React.CSSProperties = {background: '#fff', border: '1px solid #eef0f4', borderRadius: 14, padding: 14, height: '100%'};

type Ev = {id: string; type: string; category: string; title: string; body: string; url: string; amount: number | null;
    sellerId?: string; sellerName?: string; userId?: string; userName?: string; at: Date | null};

export default function LiveActivity() {
    const navigate = useNavigate();
    const weekStart = useMemo(() => moment().startOf('isoWeek').toDate(), []);
    const [snap, loading, error] = useCollection(query(
        collection(db, 'adminActivity'), where('createdAt', '>=', weekStart), orderBy('createdAt', 'desc'), limit(1000)));
    const [filter, setFilter] = useState<typeof FILTERS[number]['key']>('all');
    const [period, setPeriod] = useState<'today' | 'week'>('today');

    const events: Ev[] = useMemo(() => (snap?.docs ?? []).map((d) => {
        const x: any = d.data();
        return {id: d.id, ...x, at: x.createdAt?.toDate?.() ?? null};
    }), [snap]);

    const feed = events.filter((e) => filter === 'all' || e.category === filter).slice(0, 60);

    const board = useMemo(() => {
        const from = period === 'today' ? moment().startOf('day').toDate() : weekStart;
        const inRange = events.filter((e) => !e.at || e.at >= from);
        const paid = inRange.filter((e) => e.type === 'order_paid');
        const rank = (key: 'sellerId' | 'userId', name: 'sellerName' | 'userName') => {
            const m = new Map<string, {name: string; total: number; count: number}>();
            for (const e of paid) {
                const id = e[key];
                if (!id) continue;
                const r = m.get(id) ?? {name: e[name] || '—', total: 0, count: 0};
                r.total += e.amount || 0; r.count += 1; m.set(id, r);
            }
            return Array.from(m.entries()).map(([id, r]) => ({id, ...r})).sort((a, b) => b.total - a.total).slice(0, 5);
        };
        return {
            sales: paid.reduce((s, e) => s + (e.amount || 0), 0),
            orders: paid.length,
            users: inRange.filter((e) => e.type === 'user_registered').length,
            sellers: inRange.filter((e) => e.type === 'seller_registered').length,
            messages: inRange.filter((e) => e.category === 'support').length,
            shops: rank('sellerId', 'sellerName'),
            buyers: rank('userId', 'userName'),
        };
    }, [events, period, weekStart]);

    const chip = (active: boolean): React.CSSProperties => ({
        fontSize: 12, padding: '3px 10px', borderRadius: 999, cursor: 'pointer',
        border: `1px solid ${active ? '#29327b' : '#e5e7eb'}`, background: active ? '#29327b' : '#fff', color: active ? '#fff' : '#555',
    });
    const medal = (i: number) => ['🥇', '🥈', '🥉'][i] ?? `${i + 1}.`;

    return (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 12, marginBottom: 12}}>
            {/* Live feed */}
            <div style={card}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10, gap: 8, flexWrap: 'wrap'}}>
                    <div style={{fontWeight: 800, color: '#29327b', fontSize: 16}}>
                        <span style={{display: 'inline-block', width: 8, height: 8, borderRadius: 4, background: '#22c55e', marginRight: 6, boxShadow: '0 0 0 3px #22c55e33'}}/>
                        Live activity
                    </div>
                    <div style={{display: 'flex', gap: 6, flexWrap: 'wrap'}}>
                        {FILTERS.map((f) => <span key={f.key} style={chip(filter === f.key)} onClick={() => setFilter(f.key)}>{f.label}</span>)}
                    </div>
                </div>
                <div style={{maxHeight: 420, overflowY: 'auto'}}>
                    {loading && <div style={{color: '#999', padding: 12}}>Loading…</div>}
                    {error && <div style={{color: '#b91c1c', padding: 12}}>{error.message}</div>}
                    {!loading && feed.length === 0 && <div style={{color: '#999', padding: 12}}>Nothing yet this week — new orders, sign-ups, payouts and messages appear here as they happen.</div>}
                    {feed.map((e) => (
                        <div key={e.id} onClick={() => e.url && navigate(e.url)}
                             style={{display: 'flex', gap: 10, padding: '8px 4px', borderTop: '1px solid #f3f4f6', cursor: 'pointer'}}>
                            <span style={{width: 8, height: 8, borderRadius: 4, marginTop: 6, flex: '0 0 8px', background: DOT[e.category] ?? '#999',
                                opacity: e.type === 'order_placed' ? 0.4 : 1}}/>
                            <div style={{flex: 1, minWidth: 0}}>
                                <div style={{fontSize: 13, fontWeight: e.type === 'order_placed' ? 500 : 700, color: '#222'}}>{e.title}</div>
                                {e.body && <div style={{fontSize: 12, color: '#777', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{e.body}</div>}
                            </div>
                            <span style={{fontSize: 11, color: '#aaa', whiteSpace: 'nowrap'}}>{e.at ? moment(e.at).fromNow(true) : 'now'}</span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Leaderboard */}
            <div style={card}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10}}>
                    <div style={{fontWeight: 800, color: '#29327b', fontSize: 16}}>🏆 Leaderboard</div>
                    <div style={{display: 'flex', gap: 6}}>
                        <span style={chip(period === 'today')} onClick={() => setPeriod('today')}>Today</span>
                        <span style={chip(period === 'week')} onClick={() => setPeriod('week')}>This week</span>
                    </div>
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginBottom: 12}}>
                    {[
                        ['Paid sales', mwk(board.sales), '#16a34a'], ['Paid orders', board.orders, '#16a34a'],
                        ['New users', board.users, '#2563eb'], ['New sellers', board.sellers, '#2563eb'], ['Support', board.messages, '#ea580c'],
                    ].map(([label, value, color]) => (
                        <div key={label as string} style={{background: '#f8fafc', borderRadius: 10, padding: '8px 6px', textAlign: 'center'}}>
                            <div style={{fontSize: 15, fontWeight: 800, color: color as string, whiteSpace: 'nowrap'}}>{value}</div>
                            <div style={{fontSize: 10, color: '#888'}}>{label}</div>
                        </div>
                    ))}
                </div>
                <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12}}>
                    {[['Top shops', board.shops, (id: string) => `/shops/${id}`], ['Top buyers', board.buyers, (id: string) => `/buyers/${id}`]].map(([title, rows, link]: any) => (
                        <div key={title}>
                            <div style={{fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 4}}>{title} · by paid sales</div>
                            {rows.length === 0 && <div style={{fontSize: 12, color: '#aaa'}}>No paid orders yet</div>}
                            {rows.map((r: any, i: number) => (
                                <div key={r.id} onClick={() => navigate(link(r.id))}
                                     style={{display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderTop: i ? '1px solid #f3f4f6' : 0, cursor: 'pointer'}}>
                                    <span style={{width: 22, textAlign: 'center'}}>{medal(i)}</span>
                                    <span style={{flex: 1, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}}>{r.name}</span>
                                    <span style={{fontSize: 12, color: '#888'}}>{r.count}×</span>
                                    <span style={{fontSize: 13, fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap'}}>{mwk(r.total)}</span>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
